import {
  dedupeEvidenceRows,
  EVIDENCE_STATUS_NEEDS_REVIEW,
  type FimRevisionHistoryData,
  findingDisplay,
  findingEvidenceRows,
  needsReviewFindings,
  RevisionAuditBadgeMode,
  revisionAuditBadgeMode,
  type RevisionHistoryFinding,
  sortFindingsLatestFirst,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionHistory'
import type { RevisionFamilyMemberView } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import { matchFindingToMember } from 'features/knowledgeDocumentViewer/utils/revisionHistoryReconciliation'

export enum RevisionRowStatus {
  Missing = 'missing',
  Available = 'available',
  MayBeMissing = 'may_be_missing',
  Matched = 'matched',
}

export type RevisionAuditRow = {
  key: string
  label: string
  date: string | null
  description: string | null
  /** Null for an unknown wire status: the row renders without a pill. */
  status: RevisionRowStatus | null
  openTarget: { documentId: string; pageNum: number | undefined } | null
}

export type RevisionAudit = {
  mode: RevisionAuditBadgeMode
  history: FimRevisionHistoryData | undefined
  supportedRows: RevisionAuditRow[]
  needsReview: {
    matchedCount: number
    mayBeMissingCount: number
    /** May-be-missing rows first, then matched rows. */
    rows: RevisionAuditRow[]
  }
}

function supportedStatus(status: string | null): RevisionRowStatus | null {
  if (status === 'missing') return RevisionRowStatus.Missing
  if (status === 'available') return RevisionRowStatus.Available
  return null
}

function supportedRow(
  finding: RevisionHistoryFinding,
  index: number
): RevisionAuditRow {
  const display = findingDisplay(finding)
  return {
    key: `${finding.revision_key ?? ''}-${index}`,
    label: display.label,
    date: display.date,
    description: display.description,
    status: supportedStatus(display.status),
    openTarget: display.availableRef,
  }
}

function matchedRow(
  finding: RevisionHistoryFinding,
  member: RevisionFamilyMemberView,
  index: number
): RevisionAuditRow {
  const display = findingDisplay(finding)
  return {
    key: `matched-${finding.revision_key ?? ''}-${index}`,
    label: display.label,
    date: display.date,
    description: display.description,
    status: RevisionRowStatus.Matched,
    openTarget: { documentId: member.bqDocumentId, pageNum: member.pageNum },
  }
}

/**
 * Single owner of the revision-audit badge policy: which mode shows, which
 * findings are supported vs could-not-verify, and which could-not-verify ones
 * are already held in the system. Returns null when no badge should render
 * (no mode, or needs_review fully matched against the family). `members` are
 * expected latest-first.
 */
export function resolveRevisionAudit(
  auditState: string | null,
  history: FimRevisionHistoryData | undefined,
  members: readonly RevisionFamilyMemberView[]
): RevisionAudit | null {
  const mode = revisionAuditBadgeMode(auditState)
  if (!mode) return null

  const reviewFindings = sortFindingsLatestFirst(needsReviewFindings(history))
  const matched: RevisionAuditRow[] = []
  const unmatchedFindings: RevisionHistoryFinding[] = []
  reviewFindings.forEach((finding, index) => {
    const member = matchFindingToMember(finding, members)
    if (member) matched.push(matchedRow(finding, member, index))
    else unmatchedFindings.push(finding)
  })

  if (
    mode === RevisionAuditBadgeMode.NeedsReview &&
    reviewFindings.length > 0 &&
    unmatchedFindings.length === 0
  ) {
    return null
  }

  const mayBeMissing: RevisionAuditRow[] = dedupeEvidenceRows(
    unmatchedFindings.flatMap((f) => findingEvidenceRows(f))
  ).map((row, index) => ({
    key: `${row.label}-${row.date ?? ''}-${row.description ?? ''}-${index}`,
    ...row,
    status: RevisionRowStatus.MayBeMissing,
    openTarget: null,
  }))

  const supportedRows = sortFindingsLatestFirst(history?.findings ?? [])
    .filter((f) => f.evidence_status !== EVIDENCE_STATUS_NEEDS_REVIEW)
    .map(supportedRow)

  return {
    mode,
    history,
    supportedRows,
    needsReview: {
      matchedCount: matched.length,
      mayBeMissingCount: unmatchedFindings.length,
      rows: [...mayBeMissing, ...matched],
    },
  }
}
